import { invalidateDocumentListData, type Role } from "@prismicio/editor-fields"
import { Button, Tooltip, useAlert } from "@prismicio/editor-ui"
import { useQueryClient } from "@tanstack/react-query"
import { useTransition } from "react"

import {
  type DocumentVersionStatus,
  unpublishDocument,
} from "../../../app/services/core"
import { useRole } from "../../../app/services/user"
import { revalidateDocumentMeta } from "../../documentData"
import { useExistingDocument } from "../../DocumentProvider"
import { actionAlert, type PendingActionProps } from "./HeaderButtons"

interface UnpublishButtonProps extends PendingActionProps {}

export function UnpublishButton(props: UnpublishButtonProps) {
  const { pendingAction } = props
  const { disabled, unpublish, roleCanUnpublish, tooltip } =
    useUnpublishButton(props)

  return (
    <Tooltip
      visible={!roleCanUnpublish}
      title={tooltip.title}
      content={tooltip.content}
    >
      <Button
        size="large"
        loading={pendingAction === "unpublish"}
        disabled={disabled}
        onClick={unpublish}
      >
        Unpublish
      </Button>
    </Tooltip>
  )
}

function useUnpublishButton(props: UnpublishButtonProps) {
  const { pendingAction, onPendingActionChange } = props
  const { data, edited, status, locale } = useExistingDocument()

  const { meta } = data

  const queryClient = useQueryClient()

  const { showAlert } = useAlert()
  const [isUpdating, startTransition] = useTransition()

  const { role } = useRole(locale)

  const roleCanUnpublish = unpublishPermissions[role]

  const disabled = Boolean(
    edited || pendingAction || isUpdating || !roleCanUnpublish,
  )

  function unpublish() {
    if (!showUnpublish(status)) return

    onPendingActionChange("unpublish")

    unpublishDocument(meta.id, status)
      .then(() => {
        startTransition(() => void revalidateDocumentMeta(meta.id))

        void invalidateDocumentListData(queryClient)
      })
      .catch(() =>
        actionAlert({ action: "unpublish", showAlert, onRetry: unpublish }),
      )
      .finally(() => onPendingActionChange(undefined))
  }

  return { disabled, unpublish, roleCanUnpublish, tooltip: getTooltip(role) }
}

export function showUnpublish(
  version: DocumentVersionStatus,
): version is "published" {
  return version === "published"
}

const unpublishPermissions: Record<Role, boolean> = {
  SuperUser: true,
  Owner: true,
  Administrator: true,
  Publisher: true,
  Writer: false,
  Contributor: false,
}

function getTooltip(role: Role) {
  if (role === "Contributor")
    return {
      title: "You can't update documents",
      content:
        "Only users with the role of publisher or administrator can update documents.",
    }

  return {
    title: "You can't unpublish",
    content:
      "Only users with the role of publisher or administrator can unpublish documents.",
  }
}
